// ============================================================================
// ADD FILE SOURCE DIALOG - Upload PDF, TXT, MD, DOCX to knowledge base
// ============================================================================

import { useState, useRef } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import { knowledgeApi, type KnowledgeSource, type KnowledgeCategory } from '@/lib/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Upload, File, X } from 'lucide-react'

// ============================================================================
// CONSTANTS
// ============================================================================

const CATEGORIES: { value: KnowledgeCategory; label: string; icon: string }[] = [
  { value: 'framework', label: 'Framework', icon: '...' },
  { value: 'case_study', label: 'Case Study', icon: '...' },
  { value: 'best_practice', label: 'Best Practice', icon: '...' },
  { value: 'template', label: 'Template', icon: '...' },
  { value: 'research', label: 'Research', icon: '...' },
  { value: 'guide', label: 'Guide', icon: '...' },
  { value: 'other', label: 'Other', icon: '...' },
]

const ALLOWED_EXTENSIONS = ['.pdf', '.txt', '.md', '.docx']
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

// ============================================================================
// COMPONENT
// ============================================================================

interface AddFileSourceDialogProps {
  onClose: () => void
  onSourceCreated: (source: KnowledgeSource) => void
}

export function AddFileSourceDialog({ onClose, onSourceCreated }: AddFileSourceDialogProps) {
  const { accountId } = useSessionStore()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<KnowledgeCategory>('other')
  const [tags, setTags] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dragActive, setDragActive] = useState(false)

  const handleFileSelect = (selectedFile: File) => {
    // Validate extension
    const ext = '.' + selectedFile.name.split('.').pop()?.toLowerCase()
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setError(`Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`)
      return
    }

    // Validate size
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError(`File too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB`)
      return
    }

    setFile(selectedFile)
    setError(null)
    // Use filename as default title if empty
    if (!title) {
      setTitle(selectedFile.name.replace(/\.[^.]+$/, ''))
    }
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0])
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0])
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!accountId) {
      setError('No account selected')
      return
    }

    if (!file) {
      setError('Please select a file')
      return
    }

    try {
      setSubmitting(true)
      setError(null)

      const result = await knowledgeApi.uploadFile(accountId, {
        file,
        title: title.trim() || undefined,
        description: description.trim() || undefined,
        category,
        tags: tags
          .split(',')
          .map(t => t.trim())
          .filter(Boolean),
      })

      onSourceCreated(result.source)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload file')
    } finally {
      setSubmitting(false)
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Upload File</DialogTitle>
          <DialogDescription>
            Upload a PDF, TXT, Markdown, or DOCX file to your knowledge base.
            The content will be extracted and processed for RAG retrieval.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* File Drop Zone */}
            <div className="space-y-2">
              <Label>File *</Label>
              <div
                className={`
                  relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
                  transition-colors
                  ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-zinc-300 hover:border-zinc-400'}
                  ${file ? 'bg-green-50 border-green-300' : ''}
                `}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ALLOWED_EXTENSIONS.join(',')}
                  onChange={handleInputChange}
                  className="hidden"
                />

                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <File className="w-8 h-8 text-green-600" />
                    <div className="text-left">
                      <p className="font-medium text-zinc-900">{file.name}</p>
                      <p className="text-sm text-zinc-500">{formatFileSize(file.size)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setFile(null)
                      }}
                      className="ml-2 p-1 hover:bg-zinc-200 rounded"
                    >
                      <X className="w-4 h-4 text-zinc-500" />
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-10 h-10 mx-auto text-zinc-400" />
                    <p className="text-sm text-zinc-600">
                      <span className="font-medium text-blue-600">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-zinc-400">
                      PDF, TXT, MD, DOCX up to 10MB
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Title (optional)</Label>
              <Input
                id="title"
                placeholder="Leave blank to use filename"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select value={category} onValueChange={(val) => setCategory(val as KnowledgeCategory)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                placeholder="Brief description of the content"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label htmlFor="tags">Tags (optional)</Label>
              <Input
                id="tags"
                placeholder="product-management, strategy, discovery"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
              />
              <p className="text-xs text-zinc-500">Separate tags with commas</p>
            </div>

            {/* Error */}
            {error && (
              <p className="text-sm text-red-500">{error}</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !file}>
              {submitting ? 'Uploading...' : 'Upload File'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AddFileSourceDialog
